import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { DollarSign, AlertCircle, CheckCircle2, FileText, Loader2, Download, CreditCard, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';
import { toast } from '../hooks/use-toast';
import { useBillingPolicy } from '../hooks/useBillingPolicy';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FacturationModal = ({ open, onClose, operation, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [financialSummary, setFinancialSummary] = useState(null);

  const devisAmount = operation?.montantTotalTTC || operation?.estimatedPrice || operation?.finalPrice || 0;
  const devisHadTVA = operation?.montantTVA && operation.montantTVA > 0;

  const getDefaultDueDate = (emissionDate) => {
    const date = new Date(emissionDate);
    date.setDate(date.getDate() + 10);
    return date.toISOString().split('T')[0];
  };

  const today = new Date().toISOString().split('T')[0];

  const billingPolicy = useBillingPolicy(open);
  const vatBlocked = !!billingPolicy?.vatBlocked;
  const [applyTVA, setApplyTVA] = useState(devisHadTVA);
  const [invoiceAmount, setInvoiceAmount] = useState(devisAmount);
  const [invoiceDate, setInvoiceDate] = useState(today);
  const [paymentDueDate, setPaymentDueDate] = useState(getDefaultDueDate(today));
  const [paymentMode, setPaymentMode] = useState('');
  const [invoiceNotes, setInvoiceNotes] = useState('');
  const [cgvDocument, setCgvDocument] = useState(null);

  // PM Spec 17/02/2026 — Frais d'immobilisation
  const immoConfig = useMemo(() => {
    const fee = operation?.immobilizationFee;
    if (!fee || Number(fee) <= 0) return null;
    return {
      rate: Number(fee),
      franchise: Number(operation?.immobilizationFranchise || 0),
      unit: operation?.immobilizationFranchiseUnit || 'heures',
    };
  }, [operation]);
  const [immoExpanded, setImmoExpanded] = useState(false);
  const [immoDuration, setImmoDuration] = useState(0);
  const [immoAmountOverride, setImmoAmountOverride] = useState(null);
  const [immoNote, setImmoNote] = useState('');
  const immoAutoAmount = useMemo(() => {
    if (!immoConfig) return 0;
    const dur = Number(immoDuration) || 0;
    const billable = Math.max(0, dur - immoConfig.franchise);
    const perUnit = immoConfig.unit === 'heures' ? immoConfig.rate / 24 : immoConfig.rate;
    return Math.round(billable * perUnit);
  }, [immoConfig, immoDuration]);
  const immoFinalAmount = immoAmountOverride !== null ? Number(immoAmountOverride) : immoAutoAmount;
  const immoBillable = immoConfig ? Math.max(0, Number(immoDuration || 0) - immoConfig.franchise) : 0;

  // Modalités de paiement
  const [policies, setPolicies] = useState([]);
  const [selectedPolicyId, setSelectedPolicyId] = useState('');
  const [customPaymentText, setCustomPaymentText] = useState('');
  const selectedPolicy = policies.find(p => p.id === selectedPolicyId);
  const isCustom = selectedPolicy?.payment_type === 'custom' || selectedPolicy?.code === 'CUSTOM';

  // Charger les politiques de paiement
  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/payment-policies/`, {})
      .then(res => {
        const list = Array.isArray(res.data) ? res.data : (res.data.items || []);
        const active = list.filter(p => p.is_active !== false);
        setPolicies(active);
        if (operation?.paymentPolicyId) {
          setSelectedPolicyId(operation.paymentPolicyId);
        } else {
          const def = active.find(p => p.is_default);
          if (def) setSelectedPolicyId(def.id);
          else if (active.length > 0) setSelectedPolicyId(active[0].id);
        }
      })
      .catch(() => {});
  }, [open, operation]);

  // Sync montant avec l'opération quand le modal s'ouvre
  useEffect(() => {
    if (open && operation) {
      const amount = operation.montantTotalTTC || operation.estimatedPrice || operation.finalPrice || 0;
      setInvoiceAmount(amount);
      setApplyTVA(!!(operation.montantTVA && operation.montantTVA > 0));
      setCustomPaymentText('');
    }
  }, [open, operation]);

  const handleTVAChange = (checked) => {
    if (vatBlocked && checked) return;
    setApplyTVA(checked);
    if (operation?.montantTotalHT) {
      const ht = operation.montantTotalHT;
      const assurance = operation.insuranceAmount || 0;
      // PM Spec 17/02/2026 — l'immo s'ajoute au HT, TVA s'applique aussi dessus
      const subtotal = ht + assurance + Number(immoFinalAmount || 0);
      if (checked) {
        setInvoiceAmount(Math.round(subtotal * 1.18));
      } else {
        setInvoiceAmount(subtotal);
      }
    }
  };

  // Régime d'imposition sans TVA : on force la facture en exonération
  useEffect(() => {
    if (vatBlocked && applyTVA) handleTVAChange(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vatBlocked]);

  // PM Spec 17/02/2026 — Recalcul auto du TTC quand l'immobilisation change
  useEffect(() => {
    if (!operation?.montantTotalHT) return;
    const ht = operation.montantTotalHT;
    const assurance = operation.insuranceAmount || 0;
    const subtotal = ht + assurance + Number(immoFinalAmount || 0);
    setInvoiceAmount(applyTVA && !vatBlocked ? Math.round(subtotal * 1.18) : subtotal);
  }, [immoFinalAmount, applyTVA, operation, vatBlocked]);

  const handleInvoiceDateChange = (newDate) => {
    setInvoiceDate(newDate);
    setPaymentDueDate(getDefaultDueDate(newDate));
  };

  useEffect(() => {
    if (open && operation?.id) {
      fetchFinancialSummary();
    }
  }, [open, operation?.id]);

  const fetchFinancialSummary = async () => {
    try {
      const response = await axios.get(`${API}/financial/${operation.id}/summary`,
        {});
      setFinancialSummary(response.data);
    } catch (error) {
      console.error('Error fetching financial summary:', error);
    }
  };

  const validateForm = () => {
    if (!invoiceAmount || invoiceAmount <= 0) {
      toast({ title: "Champ manquant", description: "Le montant de la facture est obligatoire", variant: "destructive" });
      return false;
    }
    if (!invoiceDate) {
      toast({ title: "Champ manquant", description: "La date d'émission est obligatoire", variant: "destructive" });
      return false;
    }
    if (!paymentDueDate) {
      toast({ title: "Champ manquant", description: "La date d'échéance est obligatoire", variant: "destructive" });
      return false;
    }
    if (isCustom && !customPaymentText.trim()) {
      toast({ title: "Champ manquant", description: "Veuillez saisir les conditions négociées", variant: "destructive" });
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setLoading(true);

    try {
      // Step 1: Mettre à jour les infos logistiques
      await axios.post(
        `${API}/operations/${operation.id}/logistics`,
        {
          workflowStep: operation.status,
          invoiceAmount: parseFloat(invoiceAmount),
          currency: 'XOF',
          invoiceDate: new Date(invoiceDate).toISOString(),
          paymentDueDate: new Date(paymentDueDate).toISOString(),
          paymentMode: paymentMode,
          applyTVA: applyTVA && !vatBlocked,
          tvaRate: applyTVA && !vatBlocked ? 18 : 0
        },
        {}
      );

      // Step 2: Upload CGV si fourni
      if (cgvDocument) {
        const formData = new FormData();
        formData.append('file', cgvDocument);
        formData.append('documentType', 'cgv');
        await axios.post(
          `${API}/operations/${operation.id}/documents`,
          formData,
          { headers: { Authorization: 'Bearer cookie-auth', 'Content-Type': 'multipart/form-data' } }
        );
      }

      // Step 3: Générer la facture avec modalités de paiement
      setGeneratingPdf(true);
      // PM Spec 17/02/2026 — Bloc immobilisation (envoyé seulement si applicable)
      const immobilization = (immoConfig && Number(immoDuration) > 0 && immoFinalAmount > 0) ? {
        duration: Number(immoDuration),
        unit: immoConfig.unit,
        rate: immoConfig.rate,
        franchise: immoConfig.franchise,
        amount: immoAmountOverride !== null ? Number(immoAmountOverride) : null,
        note: immoNote.trim() || null,
      } : null;
      const invoiceResponse = await axios.post(
        `${API}/financial/${operation.id}/generate-invoice`,
        {
          amount: parseFloat(invoiceAmount),
          applyTVA: applyTVA && !vatBlocked,
          tauxTVA: applyTVA && !vatBlocked ? 18 : 0,
          note: invoiceNotes || `Facture générée ${applyTVA && !vatBlocked ? 'avec TVA (18 %)' : 'sans TVA'}`,
          payment_policy_id: selectedPolicyId || null,
          custom_payment_text: isCustom ? customPaymentText : null,
          immobilization,
          immobilizationIncludedInHT: false,
        },
        { headers: { Authorization: 'Bearer cookie-auth', 'Content-Type': 'application/json' } }
      );

      const invoiceNumber = invoiceResponse.data.invoice_number;

      // Step 4: Télécharger le PDF si généré
      if (invoiceResponse.data.pdf_generated) {
        try {
          const pdfResponse = await axios.get(
            `${API}/documents/download/${invoiceNumber}`,
            { responseType: 'blob' }
          );
          const url = window.URL.createObjectURL(new Blob([pdfResponse.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `${invoiceNumber || 'facture'}.pdf`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        } catch (pdfError) {
          console.warn('PDF download failed:', pdfError);
        }
      }

      toast({ title: "Facture générée", description: `Facture ${invoiceNumber} créée avec les modalités sélectionnées.` });
      onSuccess?.();
      onClose();

    } catch (error) {
      console.error('Error submitting facturation:', error);
      toast({ title: "Erreur", description: error.response?.data?.detail || "Une erreur est survenue", variant: "destructive" });
    } finally {
      setLoading(false);
      setGeneratingPdf(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Préparation de la Facturation</DialogTitle>
          <DialogDescription>
            Renseigner les informations de facturation — La facture PDF sera générée automatiquement
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-4">
            <DollarSign className="w-5 h-5 text-[#1db584]" />
            <h3 className="text-lg font-semibold">Informations de Facturation</h3>
          </div>

          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              La facture PDF sera <strong>générée automatiquement</strong> avec les modalités de paiement sélectionnées.
            </AlertDescription>
          </Alert>

          {/* Détails du devis si disponibles */}
          {operation?.montantTotalHT && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <h4 className="font-semibold text-emerald-800 mb-2">Détails du devis accepté</h4>
              <div className="space-y-1 text-sm">
                <p className="flex justify-between">
                  <span className="text-gray-700">Montant HT :</span>
                  <span className="font-medium">{operation.montantTotalHT?.toLocaleString('fr-FR')} XOF</span>
                </p>
                {operation.insuranceAmount > 0 && (
                  <p className="flex justify-between">
                    <span className="text-gray-700">Assurance :</span>
                    <span className="font-medium">{operation.insuranceAmount?.toLocaleString('fr-FR')} XOF</span>
                  </p>
                )}
                {immoConfig && (
                  <p className="flex justify-between">
                    <span className="text-gray-700">Tarif / Immobilisation :</span>
                    <span className="font-medium">
                      {immoConfig.rate.toLocaleString('fr-FR')} XOF / jour
                      {immoConfig.franchise > 0 && ` • Franchise ${immoConfig.franchise} ${immoConfig.unit}`}
                    </span>
                  </p>
                )}
                {(operation.advances || []).length > 0 && (
                  <>
                    <div className="border-t border-gray-200 my-2"></div>
                    <p className="flex justify-between text-amber-800">
                      <span className="font-medium">Acomptes déjà versés :</span>
                      <span className="font-bold">
                        – {(operation.advances || []).reduce((s, a) => s + Number(a.amount || 0), 0).toLocaleString('fr-FR')} XOF
                      </span>
                    </p>
                    <p className="text-[11px] text-amber-700 italic pl-2">
                      Détaillé sur la facture finale ({operation.advances.length} versement{operation.advances.length > 1 ? 's' : ''}) — déduits automatiquement
                    </p>
                  </>
                )}
                {devisHadTVA && (
                  <p className="flex justify-between">
                    <span className="text-gray-700">
                      TVA (18 %) du devis :
                      {vatBlocked && (
                        <span className="ml-1 text-xs text-gray-500">(non reprise sur la facture)</span>
                      )}
                    </span>
                    <span className={`font-medium ${vatBlocked ? 'text-gray-400 line-through' : ''}`}>
                      {operation.montantTVA?.toLocaleString('fr-FR')} XOF
                    </span>
                  </p>
                )}
                <p className="flex justify-between pt-2 border-t border-emerald-300">
                  <span className="font-semibold text-emerald-800">Total du devis :</span>
                  <span className="font-bold text-emerald-800">{operation.montantTotalTTC?.toLocaleString('fr-FR')} XOF</span>
                </p>
              </div>
            </div>
          )}

          {/* Option TVA — grisée si le régime d'imposition interdit la TVA */}
          <div
            className={`border rounded-lg p-4 ${vatBlocked ? 'bg-gray-100 border-gray-200' : 'bg-blue-50 border-blue-200'}`}
            data-testid="facturation-tva-block"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="applyTVA"
                  checked={applyTVA && !vatBlocked}
                  disabled={vatBlocked}
                  onChange={(e) => handleTVAChange(e.target.checked)}
                  className={`w-4 h-4 border-gray-300 rounded focus:ring-[#1db584] ${vatBlocked ? 'cursor-not-allowed opacity-60' : 'text-[#1db584]'}`}
                  data-testid="facturation-tva-checkbox"
                />
                <Label
                  htmlFor="applyTVA"
                  className={`font-medium ${vatBlocked ? 'text-gray-500 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  Appliquer la TVA (18%) sur la facture
                  {vatBlocked && (
                    <span className="ml-2 text-xs font-normal uppercase tracking-wide text-gray-500">
                      indisponible
                    </span>
                  )}
                </Label>
              </div>
              {applyTVA && !vatBlocked && operation?.montantTotalHT && (
                <span className="text-sm text-blue-700 font-medium">
                  +{((operation.montantTotalHT + (operation.insuranceAmount || 0)) * 0.18).toLocaleString('fr-FR')} XOF
                </span>
              )}
            </div>
            <p className={`text-xs mt-2 ml-7 ${vatBlocked ? 'text-gray-500' : 'text-gray-600'}`}>
              {vatBlocked
                ? billingPolicy.message
                : devisHadTVA
                  ? "La TVA était incluse dans le devis accepté par le client"
                  : "La TVA n'était pas incluse dans le devis accepté"}
            </p>
          </div>

          {/* PM Spec 17/02/2026 — Frais d'immobilisation (visible uniquement si configuré dans le devis) */}
          {immoConfig && (
            <div className="border rounded-lg overflow-hidden bg-amber-50/40 border-amber-200" data-testid="immo-section">
              <button
                type="button"
                onClick={() => setImmoExpanded(!immoExpanded)}
                className="w-full flex items-center justify-between gap-2 p-3 text-left hover:bg-amber-100/40 transition"
                data-testid="immo-toggle"
              >
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-amber-600" />
                  <span className="text-sm font-medium text-slate-800">
                    Frais d'immobilisation (du devis)
                  </span>
                  {immoFinalAmount > 0 && (
                    <Badge className="bg-amber-100 text-amber-800 border-amber-300 font-mono">
                      +{immoFinalAmount.toLocaleString('fr-FR')} XOF
                    </Badge>
                  )}
                </div>
                {immoExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              {immoExpanded && (
                <div className="p-4 space-y-3 border-t border-amber-200 text-sm">
                  <div className="flex justify-between text-xs text-slate-600">
                    <span>Tarif convenu :</span>
                    <span className="font-mono">{immoConfig.rate.toLocaleString('fr-FR')} XOF / jour</span>
                  </div>
                  <div className="flex justify-between text-xs text-slate-600">
                    <span>Franchise :</span>
                    <span className="font-mono">{immoConfig.franchise} {immoConfig.unit}</span>
                  </div>
                  <div className="space-y-1 pt-1">
                    <Label htmlFor="facturation-immo-duration" className="text-xs">
                      Durée effective d'immobilisation ({immoConfig.unit})
                    </Label>
                    <Input
                      id="facturation-immo-duration"
                      data-testid="immo-duration-input"
                      type="number"
                      min={0}
                      step="0.5"
                      value={immoDuration}
                      onChange={(e) => { setImmoDuration(e.target.value); setImmoAmountOverride(null); }}
                      className="text-right font-mono"
                      placeholder="Ex: 36"
                    />
                  </div>
                  {Number(immoDuration) > 0 && (
                    <div className="text-xs text-slate-700 bg-white rounded p-2 space-y-1 border border-amber-200">
                      <div className="flex justify-between">
                        <span>Quantité facturable :</span>
                        <span className="font-mono">
                          max(0, {Number(immoDuration)} − {immoConfig.franchise}) = <strong>{immoBillable} {immoConfig.unit}</strong>
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Montant calculé :</span>
                        <span className="font-mono font-semibold text-amber-700">
                          {immoAutoAmount.toLocaleString('fr-FR')} XOF
                        </span>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-2 pt-1">
                    <input
                      type="checkbox"
                      id="facturation-immo-override"
                      checked={immoAmountOverride !== null}
                      onChange={(e) => setImmoAmountOverride(e.target.checked ? immoAutoAmount : null)}
                      data-testid="immo-override-checkbox"
                      className="w-4 h-4"
                    />
                    <Label htmlFor="facturation-immo-override" className="text-xs cursor-pointer">
                      Ajuster manuellement le montant
                    </Label>
                  </div>
                  {immoAmountOverride !== null && (
                    <Input
                      type="number"
                      min={0}
                      value={immoAmountOverride}
                      onChange={(e) => setImmoAmountOverride(Number(e.target.value))}
                      className="text-right font-mono"
                      data-testid="immo-amount-override-input"
                    />
                  )}
                  <div className="space-y-1 pt-1">
                    <Label htmlFor="facturation-immo-note" className="text-xs">Note sur la ligne (optionnel)</Label>
                    <Textarea
                      id="facturation-immo-note"
                      data-testid="immo-note-input"
                      value={immoNote}
                      onChange={(e) => setImmoNote(e.target.value)}
                      rows={2}
                      placeholder="Ex: Immobilisation chantier Bouaké du 12 au 15/02"
                      className="text-xs"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="space-y-3">
            <div>
              <Label htmlFor="invoiceAmount">Montant TTC de la facture (XOF) *</Label>
              <Input
                id="invoiceAmount"
                type="number"
                min="0"
                step="100"
                placeholder="ex: 500000"
                value={invoiceAmount}
                onChange={(e) => setInvoiceAmount(e.target.value)}
                required
              />
            </div>

            <div>
              <Label htmlFor="invoiceDate">Date d'émission de la facture *</Label>
              <Input
                id="invoiceDate"
                type="date"
                value={invoiceDate}
                onChange={(e) => handleInvoiceDateChange(e.target.value)}
                required
              />
            </div>

            <div>
              <Label htmlFor="paymentDueDate">Date d'échéance de paiement *</Label>
              <Input
                id="paymentDueDate"
                type="date"
                value={paymentDueDate}
                onChange={(e) => setPaymentDueDate(e.target.value)}
                required
              />
              <p className="text-sm text-gray-500 mt-1">Par défaut : 10 jours après l'émission</p>
            </div>

            {/* Modalités de paiement */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5">
                <CreditCard className="w-4 h-4" /> Modalités de paiement
              </Label>
              <select
                data-testid="facturation-payment-policy-select"
                value={selectedPolicyId}
                onChange={(e) => { setSelectedPolicyId(e.target.value); setCustomPaymentText(''); }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white"
              >
                <option value="">-- Choisir une modalité --</option>
                {policies.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
              {selectedPolicy && !isCustom && (
                <p className="text-xs text-gray-500 bg-gray-50 rounded p-2 leading-relaxed">
                  {selectedPolicy.description || selectedPolicy.payment_text}
                </p>
              )}
              {isCustom && (
                <Textarea
                  value={customPaymentText}
                  onChange={(e) => setCustomPaymentText(e.target.value)}
                  placeholder="Ex: 60% avant mise à disposition, 40% à livraison..."
                  rows={2}
                  className="text-sm"
                />
              )}
            </div>

            <div>
              <Label htmlFor="paymentMode">Mode de paiement prévu (optionnel)</Label>
              <Select value={paymentMode} onValueChange={setPaymentMode}>
                <SelectTrigger id="paymentMode">
                  <SelectValue placeholder="Sélectionner un mode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="virement">Virement bancaire</SelectItem>
                  <SelectItem value="mobile_money">Mobile Money</SelectItem>
                  <SelectItem value="cheque">Chèque</SelectItem>
                  <SelectItem value="especes">Espèces</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="invoiceNotes">Notes sur la facture (optionnel)</Label>
              <Input
                id="invoiceNotes"
                type="text"
                placeholder="Notes additionnelles..."
                value={invoiceNotes}
                onChange={(e) => setInvoiceNotes(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-4 pt-4 border-t">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-5 h-5 text-[#1db584]" />
              <h3 className="text-lg font-semibold">Documents Optionnels</h3>
            </div>

            <div className="border rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="cgvDocument" className="flex items-center gap-2">
                  Conditions Générales de Vente (optionnel)
                </Label>
                {cgvDocument && <CheckCircle2 className="w-5 h-5 text-green-600" />}
              </div>
              <Input
                id="cgvDocument"
                type="file"
                accept=".pdf"
                onChange={(e) => setCgvDocument(e.target.files[0])}
                className="cursor-pointer"
              />
              {cgvDocument && (
                <p className="text-sm text-gray-600 mt-1">{cgvDocument.name}</p>
              )}
            </div>

            <Alert>
              <FileText className="h-4 w-4" />
              <AlertDescription>
                La facture sera générée au format PDF avec le numéro FACT-{new Date().getFullYear()}-XXXX
              </AlertDescription>
            </Alert>
          </div>
        </div>

        <div className="flex justify-between pt-4 border-t">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={loading || generatingPdf} data-testid="facturation-submit-btn">
            {loading || generatingPdf ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {generatingPdf ? 'Génération PDF...' : 'Traitement...'}
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Générer la facture
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FacturationModal;
